import { useEffect, useState } from 'react';
import { api } from '../api/client';

// Fetches the current user's company role and, when they're a sub_user,
// the set of menu labels their CompanyMember.permissions grants access to,
// for whichever company is currently active (see CompanyContext).
// allowedMenus is null for an owner (or while loading) meaning "unrestricted".
// hasCompany is false only when the account has no company_id at all (e.g.
// a dentist/hygienist/dental_professional) - true for every company member,
// whether they're the original owner or were invited as owner/sub_user.
export function useCompanyPermissions(enabled, activeCompanyId) {
    const [state, setState] = useState({ role: null, allowedMenus: null, hasCompany: true, loading: true });

    useEffect(() => {
        if (!enabled) {
            setState({ role: null, allowedMenus: null, hasCompany: true, loading: false });
            return;
        }

        let cancelled = false;

        const load = async () => {
            try {
                const [meRes, menusRes] = await Promise.all([
                    api.get('/api/company/members/me'),
                    api.get('/api/company/permissions'),
                ]);
                if (cancelled) return;

                if (!meRes.data.has_company) {
                    setState({ role: null, allowedMenus: null, hasCompany: false, loading: false });
                    return;
                }

                if (meRes.data.role !== 'sub_user') {
                    setState({ role: meRes.data.role, allowedMenus: null, hasCompany: true, loading: false });
                    return;
                }

                const idToMenu = new Map(menusRes.data.map((m) => [m.id, m.menu]));
                const permitted = new Set(
                    (meRes.data.permissions || []).map((id) => idToMenu.get(id)).filter(Boolean)
                );
                setState({ role: 'sub_user', allowedMenus: permitted, hasCompany: true, loading: false });
            } catch (error) {
                if (!cancelled) {
                    console.error('Failed to load company permissions:', error);
                    setState({ role: null, allowedMenus: null, hasCompany: true, loading: false });
                }
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [enabled, activeCompanyId]);

    return state;
}
